import { get } from 'lodash-es';
import { emptyArray } from 'ui-components/default-props/empty';
import historyDefault from '../../history';
import { countryValidatorsService, defaultValidator } from '../../services/country-validators-service';
import Global from '../../store/global-selectors';

export const getValidator = (shipToAddress) => {
  const validator = countryValidatorsService[shipToAddress.countryCode] || defaultValidator;
  return validator(shipToAddress);
};

const validateOrderItem = (item) => {
  const hasEmptyQuantityOrdered = item.quantityOrdered === '';
  const hasEmptyName = item.name === '';
  const hasEmptyQuantity = item.quantity === '';
  const hasInvalidPrice = !item.unitPrice?.value || item.unitPrice.value <= 0;

  return hasEmptyQuantityOrdered || hasEmptyName || hasEmptyQuantity || hasInvalidPrice;
};

export const orderPropertyToValidatorMap = {
  orderNumber: (order) => {
    return { orderNumber: !order.orderNumber };
  },
  shipToAddress: (order) => {
    const { fieldErrorHash } = getValidator(order.shipToAddress);
    return fieldErrorHash;
  },
  items: (order) => {
    const items = get(order, 'items', emptyArray);
    return items.some(validateOrderItem);
  },
};

export const getIsOrdersPage = (history = historyDefault) => {
  const pathname = history.location.pathname;

  return pathname === '/orders' || pathname.indexOf('/orders/') === 0;
};

export const getWhitelistPages = (history = historyDefault) => {
  const pathname = history.location.pathname;
  return /^\/(customers|onboard|orders|scan|shipments\/batches\/open|shipments\/batches\/processed|shipments\/batches\/archived|shipments\/batches\/advanced-search)/.test(
    pathname,
  );
};

export const getActualSalesOrderIds = (state, entityIds = []) => {
  const isWhiteListPage = getWhitelistPages();
  // This is a leftover from our transition away the initial V3 data model where some grids used salesOrderId and others used fulfillmentPlanId.
  // Products, and maybe a few others still use the old model.
  if (entityIds.length === 0 || !isWhiteListPage) {
    return entityIds;
  }

  const salesOrderIds = Global.selectEntitiesSalesOrderIdsByFulfillmentPlanIds(state, { fulfillmentPlanIds: entityIds });

  // If we can't find the id in our lookup, return it as-is, this is most likely
  // the OrderModalLoader trying to look up a salesOrderId from a deep link.
  if (salesOrderIds.length === 0) {
    return entityIds;
  }

  return salesOrderIds;
};

export const getActualFulfillmentPlanIds = (state, entityIds) => {
  if (entityIds.length === 0) {
    return entityIds;
  }

  const isOrdersPage = getIsOrdersPage();

  // Is this the orders page? If so, bail.
  if (isOrdersPage) {
    return entityIds;
  }

  const fulfillmentPlanIds = Global.selectEntitiesFulfillmentPlanIdsBySalesOrderIds(state, {
    salesOrderIds: entityIds,
  });

  // If we can't find the id in our lookup, return it as-is, this is most likely
  // the OrderModalLoader trying to look up a salesOrderId from a deep link.
  if (fulfillmentPlanIds.length === 0) {
    return entityIds;
  }

  return fulfillmentPlanIds;
};

export const shouldMakeSecondCountQuery = (notFilteredByStore, leftNavCount, appliedFilters, storeSelectedAndLocked, statusViewId) => {
  const viewsThatNeedCounts = ['shipped', 'cancelled', 'order-alerts'];
  if (
    (storeSelectedAndLocked && appliedFilters.length > 0) ||
    (appliedFilters.length > 0 && notFilteredByStore) ||
    !leftNavCount ||
    viewsThatNeedCounts.includes(statusViewId)
  ) {
    return true;
  }
  return false;
};

export const getPreviousAndNextOrdersGridIds = (fulfillmentPlanMetadata, fulfillmentPlanId, salesOrderId) => {
  const defaultSalesOrderIds = {
    nextFulfillmentPlanId: null,
    nextSalesOrderId: null,
    previousFulfillmentPlanId: null,
    previousSalesOrderId: null,
  };

  const fulfillmentPlanIndex = fulfillmentPlanMetadata.findIndex((record) => record.fulfillmentPlanId === fulfillmentPlanId);

  if (fulfillmentPlanIndex === -1) {
    return defaultSalesOrderIds;
  }

  const fulfillmentPlanRecord = fulfillmentPlanMetadata[fulfillmentPlanIndex];
  const salesOrderIndex = fulfillmentPlanRecord.salesOrdersFulfilledIds.findIndex((record) => record === salesOrderId);

  if (salesOrderIndex === -1) {
    return defaultSalesOrderIds;
  }

  const nextFulfillmentPlanId = (() => {
    if (salesOrderIndex < fulfillmentPlanRecord.salesOrdersFulfilledIds.length - 1) {
      return fulfillmentPlanId;
    }

    if (fulfillmentPlanIndex < fulfillmentPlanMetadata.length - 1) {
      return fulfillmentPlanMetadata[fulfillmentPlanIndex + 1].fulfillmentPlanId;
    }

    return null;
  })();

  const nextSalesOrderId = (() => {
    if (nextFulfillmentPlanId === fulfillmentPlanId) {
      return fulfillmentPlanRecord.salesOrdersFulfilledIds[salesOrderIndex + 1];
    }

    if (nextFulfillmentPlanId !== null) {
      return fulfillmentPlanMetadata[fulfillmentPlanIndex + 1].salesOrdersFulfilledIds[0];
    }

    return null;
  })();

  const previousFulfillmentPlanId = (() => {
    if (salesOrderIndex > 0) {
      return fulfillmentPlanId;
    }

    if (fulfillmentPlanIndex > 0) {
      return fulfillmentPlanMetadata[fulfillmentPlanIndex - 1].fulfillmentPlanId;
    }

    return null;
  })();

  const previousSalesOrderId = (() => {
    if (previousFulfillmentPlanId === fulfillmentPlanId) {
      return fulfillmentPlanRecord.salesOrdersFulfilledIds[salesOrderIndex - 1];
    }

    if (previousFulfillmentPlanId !== null) {
      const previousFulfillmentPlanSalesOrders = fulfillmentPlanMetadata[fulfillmentPlanIndex - 1].salesOrdersFulfilledIds;

      return previousFulfillmentPlanSalesOrders[previousFulfillmentPlanSalesOrders.length - 1];
    }

    return null;
  })();

  return {
    ...defaultSalesOrderIds,
    nextFulfillmentPlanId,
    nextSalesOrderId,
    previousFulfillmentPlanId,
    previousSalesOrderId,
  };
};
